import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FileText, Scale, Gavel, Activity, AlertTriangle, TrendingUp,
  BookOpenCheck, MessageCircle, Gauge, Presentation, Images,
} from "lucide-react";
import { analysesApi } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import {
  DebtCompositionDonut, DebtGdpTrajectory, CreditorsBar, SectorRadar,
  BudgetTreemap, OddScatter, EspaceSocialArea, RiskGauge,
  DebtServiceWaterfall, DebtCalendarHeatmap, DebtFlowSankey,
} from "@/components/charts/Charts";
import { Button } from "@/components/ui/button";

export default function ProjectDashboard() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    analysesApi.summary(projectId).then(setData).catch((e) => setError(e?.message || "Erreur"));
    const i = setInterval(() => analysesApi.summary(projectId).then(setData).catch(() => {}), 8000);
    return () => clearInterval(i);
  }, [projectId]);

  if (error) return <Empty msg={error} />;
  if (!data) return <Loading />;

  const { macro, vitaliste, clauses, odd, document_count, validated_count, project, aggregated } = data;
  const v = macro.viabilite || {};
  const debt = macro.debt_snapshot || {};
  const mm = macro.macro_snapshot || {};
  const social = aggregated?.social_indicators || {};

  // Composite risk score (0-100)
  const riskScore = computeRisk(v, vitaliste);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-7xl mx-auto w-full" data-testid="project-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono text-[10px] text-muted-foreground">DASHBOARD ANALYTIQUE</div>
          <h1 className="mt-1 font-serif font-bold text-3xl">
            {project.name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {project.country} · {project.period_start}–{project.period_end} · {project.currency}
            &nbsp;·&nbsp;<span className="font-mono">{document_count}</span> docs
            (<span className="font-mono">{validated_count}</span> validés)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button asChild variant="outline" size="sm"><Link to={`/app/projects/${projectId}/documents`}><FileText size={14} className="mr-2" /> Documents</Link></Button>
          <Button asChild variant="outline" size="sm"><Link to={`/app/projects/${projectId}/analyses/vitaliste`}><Scale size={14} className="mr-2" /> Vitaliste</Link></Button>
          <Button asChild variant="outline" size="sm"><Link to={`/app/projects/${projectId}/analyses/clauses`}><Gavel size={14} className="mr-2" /> Clauses</Link></Button>
          <Button asChild variant="outline" size="sm"><Link to={`/app/projects/${projectId}/infographics`}><Images size={14} className="mr-2" /> Infographies</Link></Button>
          <Button asChild variant="outline" size="sm"><Link to={`/app/projects/${projectId}/present`}><Presentation size={14} className="mr-2" /> Présenter</Link></Button>
          <Button asChild size="sm" className="bg-marine hover:bg-marine/90 text-white"><Link to={`/app/projects/${projectId}/simulator`}><Gauge size={14} className="mr-2" /> Simulateur</Link></Button>
        </div>
      </div>

      {document_count === 0 && (
        <div className="mt-8 border border-dashed border-gold/60 bg-gold/5 rounded-sm p-8 text-center" data-testid="empty-dashboard">
          <BookOpenCheck size={36} className="mx-auto text-gold" />
          <h3 className="mt-3 font-serif font-bold text-xl">Aucun document analysé</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
            Téléversez vos rapports FMI, Banque Mondiale, conventions de prêts, lois de finances.
            Le dashboard se remplira automatiquement après extraction.
          </p>
          <Button asChild className="mt-6 bg-marine hover:bg-marine/90 text-white">
            <Link to={`/app/projects/${projectId}/documents`}>Aller à la bibliothèque</Link>
          </Button>
        </div>
      )}

      {/* LIGNE 1 — Dette */}
      <Section title="INDICATEURS DETTE" subtitle="Viabilité et pression sur les recettes">
        <KpiCard testId="kpi-debt-gdp" label="DETTE / PIB" value={v.debt_gdp_ratio} unit="%"
                 band={v.debt_gdp_band} format="pct" sub="Seuil IADM 70%" />
        <KpiCard testId="kpi-service-rev" label="SERVICE / RECETTES" value={v.service_revenue_ratio} unit="%"
                 band={v.service_revenue_band} format="pct" sub="Critique > 25%" />
        <KpiCard testId="kpi-tavd" label="TAVD (V5)" value={vitaliste.V5} unit="%"
                 band={vitaliste.tavd_band} format="pct" sub="Taux d'Aliénation Vitaliste" />
        <KpiCard testId="kpi-d2" label="D2 — JOURS / CONTRIB." value={vitaliste.D2} unit="j"
                 format="days" sub="Jours de vie hypothéqués" />
      </Section>

      {/* LIGNE 2 — Vitaliste */}
      <Section title="INDICATEURS VITALISTES" subtitle="Théorie Nouvelle — A. ELY Mustapha">
        <KpiCard testId="kpi-d5" label="D5 — SERVICE EN JOURS/AN" value={vitaliste.D5} unit="j/an" format="days" sub="Jours absorbés chaque année" />
        <KpiCard testId="kpi-v6" label="V6 — ENFANT ENDETTÉ" value={vitaliste.V6} unit="j" format="days" sub="Hypothèque à la naissance" />
        <KpiCard testId="kpi-espace" label="ESPACE SOCIAL" value={vitaliste.espace_social} unit={project.currency} format="money"
                 band={vitaliste.espace_social < 0 ? "critique" : "soutenable"} sub="Recettes − service" />
        <KpiCard testId="kpi-ipsd" label="IPSD" value={vitaliste.IPSD} unit="%" format="pct" sub="Pression sociale de la dette" />
      </Section>

      {/* LIGNE 3 — Social */}
      <Section title="INDICATEURS SOCIAUX">
        <KpiCard testId="kpi-poverty" label="PAUVRETÉ" value={social.poverty_rate} unit="%" format="pct" />
        <KpiCard testId="kpi-life" label="ESPÉRANCE DE VIE" value={social.life_expectancy} unit="ans" format="pct" />
        <KpiCard testId="kpi-literacy" label="ALPHABÉTISATION" value={social.literacy_rate} unit="%" format="pct" />
        <KpiCard testId="kpi-unemployment" label="CHÔMAGE" value={social.unemployment_rate} unit="%" format="pct" />
      </Section>

      {/* CHARTS */}
      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <ChartCard title="Composition de la dette" subtitle="G1 — Multilatérale / Bilatérale / Commerciale / Domestique">
          <DebtCompositionDonut composition={macro.composition} />
        </ChartCard>
        <ChartCard title="Indice de risque composite" subtitle="G2 — Score 0-100">
          <RiskGauge value={riskScore} />
        </ChartCard>
        <ChartCard title="Trajectoire dette / PIB" subtitle="G3 — Historique + projection 5 ans">
          <DebtGdpTrajectory currentDebtGdp={v.debt_gdp_ratio} projection={macro.projection_5y} />
        </ChartCard>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <ChartCard title="Espace budgétaire social" subtitle="G10 — Recettes vs Service de la dette">
          <EspaceSocialArea macro={mm} debt={debt} vit={vitaliste} />
        </ChartCard>
        <ChartCard title="Budget national — Où va l'argent ?" subtitle="G5 — Décomposition treemap">
          <BudgetTreemap macro={mm} debt={debt} vit={vitaliste} />
        </ChartCard>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <ChartCard title="Classement des créanciers" subtitle="G7 — Par montant prêté">
          <CreditorsBar clauses={clauses?.clauses || []} />
        </ChartCard>
        <ChartCard title="Performance sectorielle sociale" subtitle="G6 — Radar">
          <SectorRadar social={social} />
        </ChartCard>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <ChartCard title="Conditionnalités vs Impact social" subtitle="G8 — Scatter">
          <OddScatter conditionalities={aggregated?.macro_indicators ? (data.aggregated_conditionalities || []) : []} />
        </ChartCard>
        <ChartCard title="Alertes clauses abusives" subtitle={`${clauses?.alerts?.length || 0} alerte(s) détectée(s)`}>
          <AlertsList alerts={clauses?.alerts || []} />
        </ChartCard>
      </div>

      {/* NEW: Waterfall + Calendar heatmap + Sankey */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <ChartCard title="Décomposition du service de la dette" subtitle="G4 — Waterfall">
          <DebtServiceWaterfall debt={debt} />
        </ChartCard>
        <ChartCard title="Échéancier — Murs de la dette" subtitle="G9 — Calendar heatmap 10 ans">
          <DebtCalendarHeatmap debt={debt} revenue={mm.public_revenue} baseYear={project.period_end} />
        </ChartCard>
      </div>

      <div className="mt-6">
        <ChartCard title="Flux de la dette" subtitle="G11 — Sankey · Créanciers → Types de financement">
          <DebtFlowSankey composition={macro.composition} clauses={clauses?.clauses || []} />
        </ChartCard>
      </div>

      {/* Interprétations vitalistes */}
      {vitaliste.interpretations?.length > 0 && (
        <div className="mt-10 border border-gold/40 bg-gold/5 rounded-sm p-6" data-testid="vitaliste-interpretations">
          <div className="label-mono text-[10px] text-gold">LECTURE VITALISTE</div>
          <ul className="mt-4 space-y-2 text-sm">
            {vitaliste.interpretations.map((m, i) => (
              <li key={i} className="flex items-start gap-3">
                <Scale size={14} className="mt-1 text-gold shrink-0" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ODD */}
      {Object.keys(odd?.matrix || {}).length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif font-bold text-2xl">Impact sur les ODD</h2>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {Object.entries(odd.matrix).map(([k, v]) => (
              <div key={k} className="border border-border rounded-sm p-3 bg-card">
                <div className={`h-2 mb-2 ${colorForBand(v.color)}`}></div>
                <div className="label-mono text-[10px] text-muted-foreground">{k}</div>
                <div className="font-mono text-xs mt-0.5">{v.occurrences} impact(s)</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mt-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="label-mono text-[10px] text-muted-foreground">{title}</h2>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5" data-testid={`chart-card-${slug(title)}`}>
      <div className="mb-3">
        <h3 className="font-serif font-bold text-base">{title}</h3>
        {subtitle && <p className="label-mono text-[9px] text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function AlertsList({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return <div className="h-[280px] grid place-items-center text-xs text-muted-foreground border border-dashed border-border rounded-sm">
      Aucune alerte — conventions conformes ou non extraites.
    </div>;
  }
  return (
    <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
      {alerts.map((a, i) => (
        <div key={i} className={`border-l-2 pl-3 py-1.5 text-xs ${
          a.severity === "critique" ? "border-alert text-alert" :
          a.severity === "eleve" ? "border-warn text-foreground" :
          "border-muted-foreground text-foreground"
        }`}>
          <div className="flex items-start gap-2">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <div>
              <div className="label-mono text-[9px] uppercase">{a.type}</div>
              <div>{a.msg}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function colorForBand(c) {
  return {
    rouge: "bg-alert", orange: "bg-warn", neutre: "bg-muted-foreground", vert: "bg-success",
  }[c] || "bg-muted";
}

function computeRisk(v, vit) {
  let score = 0, n = 0;
  if (v.debt_gdp_ratio != null) { score += Math.min(100, (v.debt_gdp_ratio / 70) * 60); n++; }
  if (v.service_revenue_ratio != null) { score += Math.min(100, (v.service_revenue_ratio / 25) * 80); n++; }
  if (vit.V5 != null) { score += Math.min(100, (vit.V5 / 400) * 90); n++; }
  if (vit.IPSD != null) { score += Math.min(100, (vit.IPSD / 50) * 70); n++; }
  return n ? score / n : 0;
}

function slug(s) { return s.toLowerCase().replace(/[^a-z0-9]+/g, "-"); }

function Loading() {
  return <div className="p-10 label-mono text-xs text-muted-foreground">Chargement du dashboard…</div>;
}
function Empty({ msg }) {
  return <div className="p-10 text-alert text-sm">{msg}</div>;
}
